import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";

import { userRoutes, authRoutes } from "./routes/allRoutes";
import Authmiddleware from "./routes/middleware/Authmiddleware";
import NonAuthLayout from "./components/NonAuthLayout";

import VerticalLayout from "components/VerticalLayout";
import { layoutTypes } from "./constants/layout";
import { Spinner } from "reactstrap";

import "./assets/scss/theme.scss";

const App = () => {
  const updateRef = useRef(false);
  const [latestVersion, setLatestVersion] = useState("");
  const [checkingVersion, setCheckingVersion] = useState(true);
  const [updateRequired, setUpdateRequired] = useState(false);
  const layoutType = useSelector(state => state.Layout.layoutType);

  const getLayout = (layoutType) => {
    switch (layoutType) {
      case layoutTypes.VERTICAL:
      default:
        return VerticalLayout;
    }
  };

  const checkVersion = async () => {
    try {
      const res = await axios.get(`/version.json?t=${Date.now()}`);
      const latestVersion = res.data.version;
      setLatestVersion(latestVersion);
      const localVersion = localStorage.getItem("app_version");

      if (!localVersion) {
        localStorage.setItem("app_version", latestVersion);
      } else if (localVersion !== latestVersion) {
        updateRef.current = true;
        setUpdateRequired(true);
      }

    } catch (err) {
      console.error("Version check failed", err);
    } finally {
      setCheckingVersion(false);
    }
  };

  const Layout = getLayout(layoutType);

  useEffect(() => {
    checkVersion();

    const interval = setInterval(() => {
      if (!updateRef.current) {
        checkVersion();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const ForceUpdateScreen = () => {
    const handleUpdate = async () => {
      localStorage.clear();
      sessionStorage.clear();

      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }

      let timerInterval;

      await Swal.fire({
        title: "Updating App!",
        html: `Please wait while the latest version is being installed...
              <br/> Do not close or refresh the page until the update is complete.
              <br/><br/>
              <b></b> seconds remaining`,
        timer: 10000,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();

          const b = Swal.getPopup().querySelector("b");

          timerInterval = setInterval(() => {
            if (b) {
              b.textContent = Math.ceil(
                Swal.getTimerLeft() / 1000
              );
            }
          }, 100);
        },
        willClose: () => {
          clearInterval(timerInterval);
        }
      });

      await Swal.fire({
        icon: "success",
        title: "Updated Successfully",
        text: "The app has been updated successfully.",
        timer: 2000,
        showConfirmButton: false
      });

      window.location.reload(true);
    };

    return (
      <div className="update-overlay">
        <div className="update-card">

          <div className="update-header">
            <div className="update-icon">
              <i className="ri-refresh-line"></i>
            </div>

            <div className="update-badge">
              NEW UPDATE AVAILABLE
            </div>

            <h2 className="update-title">
              Update your application to the latest version
            </h2>

            <p className="update-description">
              A new version of the application is available.
              Please update your app to continue using all
              features without interruption.
            </p>

            <div className="update-badge mb-0 mt-3">
              Version: {latestVersion}
            </div>

          </div>

          <div className="update-footer">
            <button
              className="update-btn"
              onClick={handleUpdate}
            >
              Update Now!
              <span className="ms-2">↻</span>
            </button>
          </div>

        </div>
      </div>
    );
  };

  if (updateRequired) {
    return <ForceUpdateScreen />;
  }

  if (checkingVersion) {
    return (
      <div style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Routes>

      {/* Auth Pages (No Sidebar) */}
      {authRoutes.map((route, idx) => (
        <Route
          path={route.path}
          element={
            <NonAuthLayout>
              {route.component}
            </NonAuthLayout>
          }
          key={idx}
        />
      ))}

      {/* User Pages (Sidebar wrapped ONCE) */}
      <Route element={<Layout />}>
        {userRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <Authmiddleware>
                {route.component}
              </Authmiddleware>
            }
            key={idx}
          />
        ))}
      </Route>

    </Routes>
  );
};

export default App;